import React, { useState } from "react";
import { createShopOrder } from "../lib/shopOrders";
import { Modal, toast } from "./UI";

const EMPTY_ITEM = { link: "", price: "", discount: "", notes: "", quantity: "1", total: "" };

export default function ShopOrderModal({ user, onClose, onSaved }) {
  const [items,  setItems]  = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const updateItem = (i, field, val) => {
    setItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      // Auto-calculate total
      if (field === "price" || field === "quantity" || field === "discount") {
        const price    = parseFloat(next[i].price) || 0;
        const qty      = parseFloat(next[i].quantity) || 1;
        const discount = parseFloat(next[i].discount) || 0;
        next[i].total  = ((price * qty) - discount).toFixed(2);
      }
      return next;
    });
  };

  const addItem    = () => setItems(p => [...p, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));

  const grandTotal = items.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0).toFixed(2);

  const save = async () => {
    if (items.some(it => !it.link.trim())) { setError("Please add a link for every item."); return; }
    setSaving(true); setError("");
    try {
      await createShopOrder({
        customerEmail: user.email,
        customerName:  user.name,
        items,
        grandTotal,
      });
      toast("Shop order submitted!");
      onSaved(); onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Shop for me" onClose={onClose} maxWidth={600}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
        Paste the links to the items you want us to buy for you. We will confirm the order and purchase on your behalf.
      </p>
      {error && <div className="error-msg">{error}</div>}

      {items.map((item, i) => (
        <div key={i} style={{ border: "0.5px solid var(--border)", borderRadius: "var(--radius)", padding: "14px", marginBottom: 12, background: "var(--bg-soft)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>Item {i + 1}</span>
            {items.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>Remove</button>}
          </div>
          <div className="form-row">
            <label>Product link *</label>
            <input value={item.link} onChange={e => updateItem(i, "link", e.target.value)} placeholder="https://amazon.com/..." />
          </div>
          <div className="grid2">
            <div className="form-row">
              <label>Price ($)</label>
              <input type="number" step="0.01" value={item.price} onChange={e => updateItem(i, "price", e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-row">
              <label>Quantity</label>
              <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} placeholder="1" />
            </div>
          </div>
          <div className="grid2">
            <div className="form-row">
              <label>Discount code</label>
              <input value={item.discount} onChange={e => updateItem(i, "discount", e.target.value)} placeholder="SAVE10" />
            </div>
            <div className="form-row">
              <label>Total ($)</label>
              <input value={item.total} onChange={e => updateItem(i, "total", e.target.value)} placeholder="0.00" style={{ fontWeight: 600 }} />
            </div>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>Notes</label>
            <input value={item.notes} onChange={e => updateItem(i, "notes", e.target.value)} placeholder="Size, color, variant..." />
          </div>
        </div>
      ))}

      <button className="btn btn-ghost" onClick={addItem} style={{ width: "100%", marginBottom: 16 }}>
        + Add another item
      </button>

      <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "12px 14px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--green-dark)" }}>{items.length} item{items.length !== 1 ? "s" : ""}</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: "var(--green-dark)" }}>Grand total: ${grandTotal}</span>
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Submitting..." : "Submit order"}
        </button>
      </div>
    </Modal>
  );
}
